
import { useState, useRef, useEffect, useCallback } from "react";
import { pkmnpricesSearch, pkmnpricesLookup, supabase } from "./supabase";

const SCAN_PROXY = "/card-scan";

const TARGET_LUMINANCE = 150;
const MAX_BRIGHTNESS_BOOST = 55;
const MAX_CONTRAST_BOOST = 25;

function adaptiveBrighten(ctx, width, height) {
  const imageData = ctx.getImageData(0, 0, width, height);
  const data = imageData.data;

  let sum = 0;
  let count = 0;
  for (let i = 0; i < data.length; i += 40) {
    sum += 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
    count++;
  }
  const avgLuminance = sum / count;

  if (avgLuminance >= TARGET_LUMINANCE) return;

  const deficit = (TARGET_LUMINANCE - avgLuminance) / TARGET_LUMINANCE;
  const brightnessBoost = Math.round(deficit * MAX_BRIGHTNESS_BOOST);
  const contrastBoost = deficit * MAX_CONTRAST_BOOST;
  const contrastFactor = (259 * (contrastBoost + 255)) / (255 * (259 - contrastBoost));

  for (let i = 0; i < data.length; i += 4) {
    for (let c = 0; c < 3; c++) {
      let v = data[i + c] + brightnessBoost;
      v = contrastFactor * (v - 128) + 128;
      data[i + c] = Math.min(255, Math.max(0, v));
    }
  }

  ctx.putImageData(imageData, 0, 0);
}

export default function CardScanner({ onSelect, onClose, S, T, mode = "inventory", batchMode = false, queueCount = 0, queueItems = [], maxQueue = null, onDone }) {
  const [phase, setPhase]       = useState("viewfinder"); // viewfinder | processing | results | error | nopermission
  const [results, setResults]   = useState([]);
  const [errorMsg, setErrorMsg] = useState("");
  const [processingStep, setProcessingStep] = useState("");
  const [extractedName, setExtractedName]   = useState("");
  const [extractedNumber, setExtractedNumber] = useState("");
  const [capturedFrame, setCapturedFrame]   = useState(null);
  const [manualQuery, setManualQuery]       = useState("");
  const [manualSearching, setManualSearching] = useState(false);
  const [countdown, setCountdown] = useState(10);

  const videoRef  = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);

  useEffect(() => {
    if (phase !== "processing") { setCountdown(10); return; }
    const id = setInterval(() => setCountdown(c => Math.max(0, c - 1)), 1000);
    return () => clearInterval(id);
  }, [phase]);

  useEffect(() => {
    let cancelled = false;
    const startCamera = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: { ideal: "environment" }, // rear camera
            width:  { ideal: 1920 },
            height: { ideal: 1080 },
          },
          audio: false,
        });
        if (cancelled) { stream.getTracks().forEach(t => t.stop()); return; }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.play().catch(() => {});
        }
      } catch (err) {
        if (!cancelled) {
          if (err.name === "NotAllowedError" || err.name === "PermissionDeniedError") {
            setPhase("nopermission");
          } else {
            setErrorMsg("Camera not available: " + err.message);
            setPhase("error");
          }
        }
      }
    };
    startCamera();
    return () => {
      cancelled = true;
      stopStream();
    };
  }, []);

  const stopStream = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
  };

  const handleCapture = useCallback(async () => {
    const video  = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;

    canvas.width  = video.videoWidth  || 1280;
    canvas.height = video.videoHeight || 720;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    adaptiveBrighten(ctx, canvas.width, canvas.height);

    const dataUrl = canvas.toDataURL("image/jpeg", 0.92);
    setCapturedFrame(dataUrl);
    stopStream();
    setPhase("processing");
    setProcessingStep("Scanning card…");

    const controller = new AbortController();
    const timeoutId = setTimeout(() => {
      controller.abort();
    }, 10000);

    try {
      const base64 = dataUrl.split(",")[1];

      let scanRes;
      try {
        const { data: { session } } = await supabase.auth.getSession();
        const authHeaders = session?.access_token
          ? { "Authorization": `Bearer ${session.access_token}` } : {};
        scanRes = await fetch(SCAN_PROXY, {
          method: "POST",
          headers: { "Content-Type": "application/json", ...authHeaders },
          body: JSON.stringify({ image: base64, mimeType: "image/jpeg" }),
          signal: controller.signal,
        });
      } catch (fetchErr) {
        if (fetchErr.name === "AbortError") {
          clearTimeout(timeoutId);
          setErrorMsg("Scan timed out. Search by card name below.");
          setPhase("error");
          return;
        }
        throw fetchErr;
      }

      if (!scanRes.ok) throw new Error("Scan request failed — try again");
      const scanData = await scanRes.json();

      if (scanData.error && !scanData.cardName) {
        throw new Error(scanData.error);
      }

      const { cardName, cardNumber } = scanData;

      if (!cardName) {
        clearTimeout(timeoutId);
        setErrorMsg("Couldn't read the card name. Hold the card flat, ensure good lighting, and make sure the card name at the top is fully visible and in focus.");
        setPhase("error");
        return;
      }

      setExtractedName(cardName);
      setExtractedNumber(cardNumber || "");
      setProcessingStep(`Searching for "${cardName}"…`);

      let found, searchErr;
      try {
        ({ results: found, error: searchErr } = await pkmnpricesSearch(cardName));
      } catch (searchFetchErr) {
        if (searchFetchErr.name === "AbortError") {
          clearTimeout(timeoutId);
          setErrorMsg("Search timed out. Search by card name below.");
          setPhase("error");
          return;
        }
        throw searchFetchErr;
      }

      clearTimeout(timeoutId);

      if (searchErr === "rate_limited") {
        setErrorMsg("Card search is temporarily unavailable. Try again in a moment.");
        setPhase("error");
        return;
      }

      if (!found?.length) {
        setErrorMsg(`No results found for "${cardName}". Try scanning again or search manually.`);
        setPhase("error");
        return;
      }

      let sorted = [...found];
      if (cardNumber) {
        const normNum = cardNumber.split("/")[0].trim().replace(/^0+/, "");
        sorted.sort((a, b) => {
          const aM = String(a.cardNumber||"").split("/")[0].replace(/^0+/,"") === normNum ? -1 : 1;
          const bM = String(b.cardNumber||"").split("/")[0].replace(/^0+/,"") === normNum ? -1 : 1;
          return aM - bM;
        });
      }

      setResults(sorted);
      setPhase("results");

    } catch (err) {
      clearTimeout(timeoutId);
      setErrorMsg(err.message || "Something went wrong. Please try again.");
      setPhase("error");
    }
  }, []);

  const retryCamera = () => {
    setCapturedFrame(null);
    setExtractedName("");
    setExtractedNumber("");
    setResults([]);
    setErrorMsg("");
    setPhase("viewfinder");
    navigator.mediaDevices.getUserMedia({
      video: { facingMode: { ideal: "environment" } },
      audio: false,
    }).then(stream => {
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.play().catch(()=>{});
      }
    }).catch(() => setPhase("nopermission"));
  };

  const fmt = n => n != null ? `$${parseFloat(n).toFixed(2)}` : null;

  if (phase === "nopermission") return (
    <div style={overlay}>
      <button onClick={onClose} style={closeBtn}>×</button>
      <div style={{textAlign:"center", padding:32, maxWidth:300}}>
        <div style={{fontSize:36, marginBottom:16}}>📷</div>
        <div style={{fontSize:16, fontWeight:600, color:"#fff", marginBottom:8}}>
          Camera access needed
        </div>
        <div style={{fontSize:15, color:"rgba(255,255,255,0.55)", lineHeight:1.7, marginBottom:24}}>
          Please allow camera access when prompted, or go to your browser settings and enable camera for this site.
        </div>
        <button onClick={onClose} style={cancelBtn}>Go Back</button>
      </div>
    </div>
  );

  if (phase === "viewfinder") return (
    <div style={overlay}>
      <canvas ref={canvasRef} style={{display:"none"}} />

      <video
        ref={videoRef}
        autoPlay
        playsInline
        muted
        style={{
          position:"absolute", inset:0,
          width:"100%", height:"100%",
          objectFit:"cover",
        }}
      />

      <div style={{
        position:"absolute", inset:0,
        background:"rgba(0,0,0,0.25)",
        display:"flex", flexDirection:"column",
        alignItems:"center", justifyContent:"center",
      }}>
        <div style={{
          width:"78%", maxWidth:300,
          aspectRatio:"2.5/3.5",
          border:"2px solid rgba(255,255,255,0.9)",
          borderRadius:12,
          boxShadow:"0 0 0 9999px rgba(0,0,0,0.25)",
          position:"relative",
        }}>
          {[
            {top:-2,left:-2,borderTop:"3px solid #fff",borderLeft:"3px solid #fff",borderRadius:"4px 0 0 0"},
            {top:-2,right:-2,borderTop:"3px solid #fff",borderRight:"3px solid #fff",borderRadius:"0 4px 0 0"},
            {bottom:-2,left:-2,borderBottom:"3px solid #fff",borderLeft:"3px solid #fff",borderRadius:"0 0 0 4px"},
            {bottom:-2,right:-2,borderBottom:"3px solid #fff",borderRight:"3px solid #fff",borderRadius:"0 0 4px 0"},
          ].map((style, i) => (
            <div key={i} style={{position:"absolute", width:22, height:22, ...style}} />
          ))}
        </div>

        <div style={{
          marginTop:24, fontSize:16, color:"rgba(255,255,255,0.8)",
          textAlign:"center", lineHeight:1.5,
          textShadow:"0 1px 4px rgba(0,0,0,0.8)",
        }}>
          Align card inside the frame
        </div>
      </div>

      {!batchMode && <button onClick={onClose} style={closeBtn}>×</button>}

      {batchMode && (
        <>
          <div style={{
            position:"absolute", top:0, left:0, right:0,
            background:"rgba(0,0,0,0.75)", padding:"14px 14px 10px",
          }}>
            <div style={{display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:10}}>
              <div style={{color:"#fff", fontSize:18, fontWeight:700, fontFamily:"'Sora',sans-serif"}}>
                {queueCount}{maxQueue ? ` / ${maxQueue}` : ""} scanned
              </div>
              <button onClick={onDone} style={{
                background:"#4ade80", border:"none", color:"#0d1117",
                borderRadius:14, padding:"14px 26px",
                fontSize:18, fontWeight:700, cursor:"pointer",
                fontFamily:"'Sora',sans-serif",
              }}>
                Done →
              </button>
            </div>
            {queueItems.length > 0 && (
              <div style={{display:"flex", gap:8, overflowX:"auto", paddingBottom:4}}>
                {queueItems.map((c, i) => (
                  <div key={c._key || i} style={{flexShrink:0, width:48, textAlign:"center"}}>
                    <div style={{width:48, height:64, borderRadius:6, overflow:"hidden",
                      background:"#1a1f2e", border:"1px solid rgba(255,255,255,0.2)",
                      display:"flex", alignItems:"center", justifyContent:"center"}}>
                      {c.ImageURL
                        ? <img src={c.ImageURL} alt="" style={{width:"100%",height:"100%",objectFit:"cover"}} />
                        : <span style={{fontSize:20}}>🃏</span>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}

      <div style={{
        position:"absolute", bottom:0, left:0, right:0,
        padding:"20px 24px 40px",
        display:"flex", alignItems:"center",
        justifyContent: batchMode ? "space-between" : "center",
        background:"linear-gradient(to top, rgba(0,0,0,0.7) 0%, transparent 100%)",
      }}>
        {batchMode && (
          <button onClick={onClose} style={{
            background:"rgba(255,255,255,0.15)", border:"1px solid rgba(255,255,255,0.3)",
            color:"#fff", borderRadius:100, padding:"14px 26px",
            fontSize:16, fontWeight:700, cursor:"pointer",
            fontFamily:"'Sora',sans-serif", flexShrink:0,
          }}>
            Cancel
          </button>
        )}
        <button
          onClick={handleCapture}
          style={{
            width:72, height:72, borderRadius:"50%",
            background:"#fff", border:"4px solid rgba(255,255,255,0.4)",
            cursor:"pointer", flexShrink:0,
            boxShadow:"0 4px 20px rgba(0,0,0,0.4)",
            display:"flex", alignItems:"center", justifyContent:"center",
            margin: batchMode ? "0 auto" : 0,
          }}
        >
          <div style={{
            width:52, height:52, borderRadius:"50%",
            background:"#fff", border:"3px solid #ccc",
          }} />
        </button>
        {batchMode && <div style={{width:98, flexShrink:0}} />}
      </div>
    </div>
  );

  if (phase === "processing") return (
    <div style={overlay}>
      {capturedFrame && (
        <img src={capturedFrame} alt="Captured"
          style={{position:"absolute",inset:0,width:"100%",height:"100%",
            objectFit:"cover", opacity:0.35}} />
      )}
      <div style={{position:"relative", textAlign:"center", padding:32}}>
        <div style={{fontSize:15, fontWeight:600, color:"#fff", marginBottom:16}}>
          {processingStep}
        </div>
        <div style={{display:"flex", gap:8, justifyContent:"center", marginBottom:16}}>
          {[0,1,2].map(i => (
            <div key={i} style={{
              width:10, height:10, borderRadius:"50%", background:"#fff",
              animation:`dot-pulse 1.2s ease-in-out ${i*0.2}s infinite`,
              opacity:0.3,
            }} />
          ))}
        </div>
        <div style={{fontSize:14, color:"rgba(255,255,255,0.5)"}}>
          {countdown > 0 ? `Timing out in ${countdown}s…` : "Wrapping up…"}
        </div>
      </div>
      <style>{`
        @keyframes dot-pulse {
          0%,80%,100%{opacity:0.2;transform:scale(0.8)}
          40%{opacity:1;transform:scale(1.15)}
        }
      `}</style>
    </div>
  );

  const manualSearch = async () => {
    if (!manualQuery.trim()) return;
    setManualSearching(true);
    try {
      const { results: found, error: searchErr } = await pkmnpricesSearch(manualQuery.trim());
      if (searchErr === "rate_limited") {
        setErrorMsg("Card search is temporarily unavailable. Try again in a moment.");
        setManualSearching(false);
        return;
      }
      if (!found?.length) {
        setErrorMsg(`No results found for "${manualQuery}". Try a different name.`);
        setManualSearching(false);
        return;
      }
      setResults(found);
      setExtractedName(manualQuery.trim());
      setExtractedNumber("");
      setPhase("results");
    } catch(e) {
      setErrorMsg("Search failed. Please try again.");
    }
    setManualSearching(false);
  };

  if (phase === "error") return (
    <div style={overlay}>
      <button onClick={onClose} style={closeBtn}>×</button>
      <div style={{textAlign:"center", padding:"0 32px", maxWidth:340, width:"100%"}}>
        <div style={{fontSize:32, marginBottom:12}}>😕</div>
        <div style={{fontSize:16, fontWeight:600, color:"#fff", marginBottom:8}}>
          Scan didn't work
        </div>
        <div style={{fontSize:15, color:"rgba(255,255,255,0.6)",
          lineHeight:1.7, marginBottom:20}}>
          {errorMsg}
        </div>

        <div style={{
          background:"rgba(255,255,255,0.06)", borderRadius:12,
          padding:"16px", marginBottom:16, textAlign:"left",
        }}>
          <div style={{fontSize:14, fontWeight:600, color:"rgba(255,255,255,0.7)",
            marginBottom:10}}>
            🔍 Search by name instead
          </div>
          <div style={{display:"flex", gap:8}}>
            <input
              style={{
                flex:1, padding:"10px 12px", borderRadius:8,
                background:"rgba(255,255,255,0.1)",
                border:"0.5px solid rgba(255,255,255,0.2)",
                color:"#fff", fontSize:15,
                fontFamily:"'Sora',sans-serif",
              }}
              placeholder={extractedName || "e.g. Charizard ex"}
              value={manualQuery}
              onChange={e => setManualQuery(e.target.value)}
              onKeyDown={e => e.key === "Enter" && manualSearch()}
            />
            <button
              onClick={manualSearch}
              disabled={manualSearching || !manualQuery.trim()}
              style={{
                background:"#3C66EE", border:"none", color:"#fff",
                borderRadius:8, padding:"10px 14px", fontSize:15,
                fontWeight:600, cursor:"pointer",
                fontFamily:"'Sora',sans-serif",
                opacity: manualSearching ? 0.6 : 1,
              }}>
              {manualSearching ? "…" : "Go"}
            </button>
          </div>
        </div>

        {extractedName && (
          <div style={{
            background:"rgba(255,255,255,0.06)", borderRadius:10,
            padding:"10px 16px", marginBottom:16,
          }}>
            <div style={{fontSize:12, color:"rgba(255,255,255,0.4)", marginBottom:4}}>
              Text detected from scan
            </div>
            <div style={{fontSize:15, color:"#88bdf2", fontWeight:600}}>{extractedName}</div>
            {extractedNumber && (
              <div style={{fontSize:13, color:"rgba(255,255,255,0.4)"}}>#{extractedNumber}</div>
            )}
          </div>
        )}

        <div style={{display:"flex", flexDirection:"column", gap:10, width:"100%"}}>
          <button onClick={retryCamera}
            style={{background:"rgba(255,255,255,0.12)", border:"0.5px solid rgba(255,255,255,0.2)",
              color:"#fff", borderRadius:12, padding:"12px", fontSize:16, fontWeight:600,
              cursor:"pointer", fontFamily:"'Sora',sans-serif"}}>
            📷 Try Scanning Again
          </button>
          <button onClick={onClose} style={cancelBtn}>Cancel</button>
        </div>
      </div>
    </div>
  );

  return (
    <div style={{
      position:"fixed", inset:0, zIndex:10000,
      background:T?.surface || "#0d1117",
      display:"flex", flexDirection:"column",
      fontFamily:"'Sora',sans-serif",
    }}>
      <div style={{
        display:"flex", alignItems:"center",
        justifyContent:"space-between",
        padding:"14px 16px",
        borderBottom:`1px solid ${T?.border || "rgba(255,255,255,0.1)"}`,
        flexShrink:0,
      }}>
        <div>
          <div style={{fontSize:16, fontWeight:600, color:T?.text || "#fff"}}>
            Results for "{extractedName}"
          </div>
          {extractedNumber && (
            <div style={{fontSize:13, color:T?.textFaint || "#666", marginTop:2}}>
              Card # {extractedNumber} detected
            </div>
          )}
        </div>
        <button onClick={onClose} style={{
          background:"none", border:"none",
          color:T?.textMuted || "#888", fontSize:22, cursor:"pointer",
        }}>×</button>
      </div>

      {batchMode && (
        <div style={{padding:"0 16px", flexShrink:0}}>
          <div style={{
            display:"inline-block", background:"rgba(60,102,238,0.15)",
            color:"#88bdf2", borderRadius:10, padding:"6px 14px",
            fontSize:15, fontWeight:700, marginTop:4,
          }}>
            {queueCount}{maxQueue ? ` / ${maxQueue}` : ""} scanned so far
          </div>
        </div>
      )}

      <div style={{padding:"10px 16px 0", flexShrink:0}}>
        <button onClick={retryCamera} style={{
          background:"none",
          border:`0.5px solid ${T?.border || "rgba(255,255,255,0.1)"}`,
          color:T?.textMuted || "#888", borderRadius:8,
          padding:"7px 14px", fontSize:14, cursor:"pointer",
          fontFamily:"'Sora',sans-serif",
        }}>
          📷 Scan again
        </button>
      </div>

      <div style={{flex:1, overflowY:"auto", padding:"12px 16px 32px"}}>
        <div style={{
          display:"grid",
          gridTemplateColumns:"repeat(auto-fill, minmax(140px, 1fr))",
          gap:12,
        }}>
          {results.map((r, i) => {
            const numMatch = extractedNumber &&
              String(r.cardNumber||"").split("/")[0].replace(/^0+/,"") ===
              extractedNumber.split("/")[0].replace(/^0+/,"");
            return (
              <button key={r.tcggoId || i}
                onClick={async () => {
                  let priced = r;
                  if (r.tcggoId && r.center == null) {
                    const result = await pkmnpricesLookup(r.cardName, r.cardNumber, r.setName, r.tcggoId);
                    if (result) priced = { ...r, ...result };
                  }
                  onSelect(priced);
                  if (batchMode) retryCamera();
                }}
                style={{
                  background: T?.surface2 || "#1a1f2e",
                  border: `0.5px solid ${numMatch
                    ? "rgba(60,102,238,0.6)"
                    : T?.border || "rgba(255,255,255,0.08)"}`,
                  borderRadius:12, padding:10,
                  cursor:"pointer", textAlign:"left",
                  display:"flex", flexDirection:"column", gap:6,
                }}>
                <div style={{
                  width:"100%", aspectRatio:"5/7", borderRadius:8,
                  overflow:"hidden",
                  background:T?.surface || "#0d1117",
                  display:"flex", alignItems:"center", justifyContent:"center",
                }}>
                  {r.imageUrl
                    ? <img src={r.imageUrl} alt={r.cardName}
                        style={{width:"100%",height:"100%",objectFit:"cover"}} />
                    : <span style={{fontSize:28}}>🃏</span>}
                </div>
                <div style={{
                  fontSize:14, fontWeight:600, color:T?.text || "#fff",
                  lineHeight:1.3,
                  display:"-webkit-box", WebkitLineClamp:2,
                  WebkitBoxOrient:"vertical", overflow:"hidden",
                }}>
                  {r.cardName}
                </div>
                <div style={{fontSize:12, color:T?.textFaint || "#666"}}>
                  {r.setName}{r.cardNumber ? ` · #${r.cardNumber}` : ""}
                </div>
                {r.price != null && (
                  <div style={{fontSize:14, fontWeight:700, color:"#4ade80"}}>
                    ${parseFloat(r.price).toFixed(2)}
                  </div>
                )}
                {numMatch && (
                  <div style={{
                    fontSize:11, fontWeight:700, color:"#3C66EE",
                    background:"rgba(60,102,238,0.12)",
                    border:"0.5px solid rgba(60,102,238,0.3)",
                    borderRadius:4, padding:"2px 6px",
                  }}>✓ Number match</div>
                )}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

const overlay = {
  position:"fixed", inset:0, zIndex:10000,
  height:"100dvh", width:"100vw",
  background:"#000",
  display:"flex", flexDirection:"column",
  alignItems:"center", justifyContent:"center",
  fontFamily:"'Sora',sans-serif",
};

const closeBtn = {
  position:"absolute", top:16, right:16,
  background:"rgba(0,0,0,0.5)", border:"0.5px solid rgba(255,255,255,0.2)",
  color:"#fff", fontSize:22, width:40, height:40,
  borderRadius:"50%", cursor:"pointer", lineHeight:1,
  display:"flex", alignItems:"center", justifyContent:"center",
  zIndex:1,
};

const cancelBtn = {
  background:"none", border:"0.5px solid rgba(255,255,255,0.2)",
  color:"rgba(255,255,255,0.5)", borderRadius:12,
  padding:"12px", fontSize:15, cursor:"pointer",
  fontFamily:"'Sora',sans-serif", width:"100%",
};
